import {useCallback, useEffect, useMemo} from 'react';
import {useDebounce} from 'use-debounce';
import {useQuery} from '@shopify/react-graphql';
import {useMonorail} from 'packages/monorail';
import {useSessionId} from '@web-utilities/session-id';
import {toPascalCase} from '@web-utilities/string';
import {
  nodesFromEdges,
  parseGidType,
} from '@shopify/admin-graphql-api-utilities';
import {redirectToAdminUrl} from 'utilities/redirect';
import {
  AppliedFilterInterface,
  Box,
  ChoiceList,
  Pagination,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  BlockStack,
  Link,
  Divider,
} from '@shopify/polaris';
import {useShopLocalesAndMarkets} from 'utilities/use-shop-locales-and-markets';
import {FilterSectionLayout} from 'components/FilterSectionLayout';
import {useI18n} from '@shopify/react-i18n';
import {TranslatableResourceType} from 'types';
import {useSearch} from 'features/Translations/hooks';
import {useMediaLayout} from '@shopify/react-hooks';

import {NoResults} from '../NoResults';
import {getThemeAssetCategoryName} from '../../utils';
import {SearchInput} from '../../../SearchInput';

import TranslatableResourceSearchQuery from './graphql/TranslatableResourceSearchQuery.graphql';
import {SearchResult} from './types';
import {GroupedSearchResult} from './components';
import {filtersBuilder, type Filter} from './utils';

export interface GroupedSearchResults {
  [key: string]: SearchResult[];
}

const DEFAULT_PAGINATION_LIMIT = 10;
const scope = 'SidebarSearch';

const filterableResourceTypes = [
  TranslatableResourceType.Collection,
  TranslatableResourceType.EmailTemplate,
  TranslatableResourceType.Metaobject,
  TranslatableResourceType.OnlineStoreArticle,
  TranslatableResourceType.OnlineStoreBlog,
  TranslatableResourceType.OnlineStoreMenu,
  TranslatableResourceType.OnlineStorePage,
  TranslatableResourceType.OnlineStoreThemeJsonTemplate,
  TranslatableResourceType.OnlineStoreThemeLocaleContent,
  TranslatableResourceType.OnlineStoreThemeSectionGroup,
  TranslatableResourceType.Product,
];

export function Search({loading}: {loading: boolean}) {
  const monorail = useMonorail();
  const sessionId = useSessionId();
  const [i18n] = useI18n();
  const {locales} = useShopLocalesAndMarkets();
  const {
    query,
    localeFilter,
    setLocaleFilter,
    resourceTypeFilter,
    setResourceTypeFilter,
    isActive,
  } = useSearch();
  const mobile = useMediaLayout('(max-width: 48rem)');
  const [debouncedUserQuery] = useDebounce(query, 500);

  const queryParams = useMemo(() => {
    const parts = [];

    if (debouncedUserQuery) {
      parts.push(`value:"${debouncedUserQuery}"`);
    }

    const filters = filtersBuilder(localeFilter, resourceTypeFilter);
    if (filters) {
      parts.push(filters);
    }

    return parts.join(' AND ');
  }, [debouncedUserQuery, localeFilter, resourceTypeFilter]);

  const {
    data,
    loading: searchQueryLoading,
    fetchMore,
  } = useQuery(TranslatableResourceSearchQuery, {
    variables: {
      query: queryParams,
      first: DEFAULT_PAGINATION_LIMIT,
    },
    skip: !debouncedUserQuery,
    notifyOnNetworkStatusChange: true,
  });

  const {hasNextPage, hasPreviousPage, startCursor, endCursor} = useMemo(
    () =>
      data?.translatableResourcesSearch?.pageInfo || {
        startCursor: undefined,
        endCursor: undefined,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    [data],
  );

  const onNext = useCallback(() => {
    fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (prev, {fetchMoreResult}) => {
        if (!fetchMoreResult) return prev;
        return fetchMoreResult;
      },
    });
  }, [endCursor, fetchMore]);

  const onPrevious = useCallback(() => {
    fetchMore({
      variables: {
        first: undefined,
        last: DEFAULT_PAGINATION_LIMIT,
        before: startCursor,
      },
      updateQuery: (prev, {fetchMoreResult}) => {
        if (!fetchMoreResult) return prev;
        return fetchMoreResult;
      },
    });
  }, [startCursor, fetchMore]);

  const produceSearchMonorailEvent = useCallback(
    (resultsViewed: boolean, resultClicked: boolean) => {
      monorail.produce({
        schemaId: 'translate_and_adapt_search/1.0',
        payload: {
          searchTerm: debouncedUserQuery,
          searchResultClicked: resultClicked,
          searchResultsViewed: resultsViewed,
          sessionToken: sessionId,
        },
      });
    },
    [debouncedUserQuery, monorail, sessionId],
  );

  useEffect(() => {
    if (searchQueryLoading || !data?.translatableResourcesSearch.edges.length)
      return;
    produceSearchMonorailEvent(true, false);
  }, [searchQueryLoading, data, produceSearchMonorailEvent]);

  const results = useMemo(() => {
    return Object.values(
      nodesFromEdges(data?.translatableResourcesSearch.edges || []).reduce(
        (acc, result) => {
          if (result.key === 'handle') return acc;
          const groupingKey =
            result.translatableParentResourceId || result.resourceId;
          const resourceType = parseGidType(result.resourceId);
          const key =
            resourceType ===
            toPascalCase(TranslatableResourceType.OnlineStoreThemeLocaleContent)
              ? getThemeAssetCategoryName(result.key.split('.')[0])
              : '';
          acc[`${groupingKey}${key}`] = [
            ...(acc[`${groupingKey}${key}`] || []),
            result,
          ];
          return acc;
        },
        {} as GroupedSearchResults,
      ),
    ).map((groupedResults) => (
      <GroupedSearchResult
        key={`${groupedResults[0].resourceId}-${groupedResults[0].key}`}
        groupedResults={groupedResults}
        onResultClick={() => produceSearchMonorailEvent(false, true)}
      />
    ));
  }, [data?.translatableResourcesSearch.edges, produceSearchMonorailEvent]);

  const paginator = useMemo(
    () =>
      results.length ? (
        <Box paddingBlockStart="400" paddingBlockEnd="400">
          <BlockStack inlineAlign="center">
            <Pagination
              hasPrevious={hasPreviousPage}
              onPrevious={onPrevious}
              hasNext={hasNextPage}
              onNext={onNext}
            />
          </BlockStack>
        </Box>
      ) : null,
    [hasNextPage, hasPreviousPage, results.length, onNext, onPrevious],
  );

  const removeFilter = useCallback((prev: Filter, filterValue: string) => {
    if (prev?.value.includes(filterValue)) {
      const newFilter = prev.value.filter((value) => value !== filterValue);

      if (newFilter.length === 0) {
        return null;
      }

      return {
        ...prev,
        value: newFilter,
      };
    }

    return prev;
  }, []);

  const filters = [
    {
      key: 'locale' as const,
      label: i18n.translate('filtersLabels.locale', {scope}),
      choices: locales.map((locale) => ({
        value: locale.locale,
        label: locale.name,
      })),
      setFilter: setLocaleFilter,
      selected: localeFilter ? localeFilter.value : [],
    },
    {
      key: 'resource_type' as const,
      label: i18n.translate('filtersLabels.resourceType', {scope}),
      choices: filterableResourceTypes
        .map((resourceType) => ({
          value: resourceType,
          label: i18n.translate(`ResourceTypes.${resourceType}`, {count: 2}),
        }))
        .sort((resourceA, resourceB) =>
          resourceA.label.localeCompare(resourceB.label),
        ),
      setFilter: setResourceTypeFilter,
      selected: resourceTypeFilter ? resourceTypeFilter.value : [],
    },
  ].map(({key, label, choices, setFilter, selected}) => ({
    key,
    label,
    shortcut: true,
    filter: (
      <ChoiceList
        title={
          <Text as="span" fontWeight="semibold" variant="bodyMd">
            {label}
          </Text>
        }
        allowMultiple
        choices={choices}
        selected={selected}
        onChange={(selected) => {
          setFilter(selected.length ? {key, value: selected} : null);
        }}
      />
    ),
    onClear: () => setFilter(null),
  }));

  const appliedFilters = useMemo(() => {
    const appliedFilters: AppliedFilterInterface[] = [];
    if (localeFilter) {
      localeFilter.value.forEach((filteredLocale) => {
        appliedFilters.push({
          key: `${localeFilter.key}-${filteredLocale}`,
          label:
            locales.find((locale) => locale.locale === filteredLocale)?.name ??
            filteredLocale,
          onRemove: () =>
            setLocaleFilter((prev) => removeFilter(prev, filteredLocale)),
        });
      });
    }

    if (resourceTypeFilter) {
      resourceTypeFilter.value.forEach((filteredResourceType) => {
        appliedFilters.push({
          key: `${resourceTypeFilter.key}-${filteredResourceType}`,
          label: i18n.translate(`ResourceTypes.${filteredResourceType}`, {
            count: 2,
          }),
          onRemove: () =>
            setResourceTypeFilter((prev) =>
              removeFilter(prev, filteredResourceType),
            ),
        });
      });
    }
    return appliedFilters;
  }, [
    localeFilter,
    resourceTypeFilter,
    locales,
    setLocaleFilter,
    removeFilter,
    i18n,
    setResourceTypeFilter,
  ]);

  if (!mobile && !isActive) return null;

  const searchInputMarkup = mobile ? (
    <SearchInput mobile disabled={loading} />
  ) : null;

  const loadingMarkup = searchQueryLoading ? (
    <Box borderBlockStartWidth="025" borderColor="border-secondary">
      <SearchResultSkeleton items={7} />
    </Box>
  ) : null;

  const placeholderMarkup = isActive ? (
    <Box padding="600">
      <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
        {i18n.translate('placeholder', {scope})}
      </Text>
    </Box>
  ) : null;

  const resultsMarkup = results.length ? (
    <Box borderBlockStartWidth="025" borderColor="border-secondary">
      {results}
    </Box>
  ) : (
    <NoResults
      heading={i18n.translate('emptySearchResults.heading', {
        scope,
      })}
      body={i18n.translate('SidebarSearch.emptySearchResults.body', {
        languagesLink: (
          <Link
            onClick={() => {
              redirectToAdminUrl({
                adminUrl: '/settings/languages',
              });
            }}
          >
            {i18n.translate('emptySearchResults.languages', {scope})}
          </Link>
        ),
      })}
    />
  );

  const totalCount = data?.translatableResourcesSearch.totalCount;
  const filterDetails = (() => {
    if (searchQueryLoading) {
      return i18n.translate('filterDetails.loading', {scope});
    }

    if (typeof totalCount === 'number' && !Number.isNaN(totalCount)) {
      return i18n.translate(
        'filterDetails.resultCount',
        {
          scope,
        },
        {count: data?.translatableResourcesSearch.totalCount},
      );
    }

    return null;
  })();

  const filtersMarkup = debouncedUserQuery ? (
    <Box
      paddingBlockEnd="300"
      paddingBlockStart="300"
      paddingInlineEnd="400"
      paddingInlineStart="400"
    >
      <FilterSectionLayout
        detailedLayout
        filterDetails={filterDetails}
        filterOptions={{
          filters,
          appliedFilters,
          hideTags: false,
          hideQueryField: true,
          disabled: loading,
          alwaysVisibleClearButton: true,
        }}
      />
    </Box>
  ) : null;

  const contentMarkup = (() => {
    if (loadingMarkup) return;

    if (debouncedUserQuery) return resultsMarkup;
    return placeholderMarkup;
  })();

  return (
    <Box>
      {searchInputMarkup && (
        <>
          {searchInputMarkup}
          <Divider />
        </>
      )}
      {filtersMarkup}
      {loadingMarkup}
      {contentMarkup}
      {paginator}
    </Box>
  );
}

export function SearchResultSkeleton({items}: {items: number}) {
  return Array(items)
    .fill(0)
    .map((_, idx) => (
      <Box
        paddingBlockStart="400"
        paddingBlockEnd="400"
        // eslint-disable-next-line react/no-array-index-key
        key={`skeleton_${idx}`}
      >
        <Box paddingInlineEnd="400" paddingInlineStart="400">
          <BlockStack gap="200">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={1} />
            <SkeletonBodyText lines={1} />
          </BlockStack>
        </Box>
      </Box>
    ));
}
